/** Use to download an image without navigating away from current tab */
export const downloadImage = async (imagePath: string, imageName: string) => {
    if (imagePath === null) {
        throw new Error("Image path is null");
    }
    const image = await fetch(imagePath);
    const imageBlog = await image.blob();
    const imageURL = URL.createObjectURL(imageBlog);
    const link = document.createElement("a");
    // eslint-disable-next-line iventis/no-href-assignments
    link.href = imageURL;
    link.download = imageName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Downloads text which is already held in memory, without navigating away from the current page.
 * The one place a file is made from text, so the json and csv downloads build their content and hand it here.
 *
 * @param content - the text of the file
 * @param fileName - name of the file to be created
 * @param mimeType - what the text is, such as application/json
 */
export function downloadTextFile(content: string, fileName: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    // eslint-disable-next-line iventis/no-href-assignments
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Gets and downloads a json file from a given url
 *
 * @param jsonFileUrl - url of the json file to be downloaded
 * @param fileName - name of the file to be downloaded
 * @returns a boolean indicating if the download was successful
 */
export async function downloadJson(jsonFileUrl: string, fileName: string): Promise<boolean> {
    // Get json file contents
    const response = await fetch(jsonFileUrl);

    // Check if response is successful
    if (response.status !== 200) {
        return false;
    }

    const data = await response.json();

    downloadTextFile(JSON.stringify(data), fileName, "application/json");
    return true;
}

/**
 * Gets and downloads a file from a given source.
 * Used for files that are too large to be downloaded through the REST API and require a signed url to be downloaded from the storage service directly.
 * @param fileUrl URL of the file to be downloaded
 * @param fileName What to name the file once downloaded for the user
 */
export const downloadLargeFile = async (fileUrl: string, fileName: string) => {
    if (!fileUrl) {
        throw new Error("File path is null");
    }
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    // eslint-disable-next-line iventis/no-href-assignments
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/** Use to download a file, don't use for images */
export const downloadFile = (filePath: string) => window.open(filePath);
